import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();

  // Map language names to language codes
  const languageMap = {
    'English': 'en',
    'Arabic': 'ar',
    'Spanish': 'es',
    'French': 'fr',
  };

  // Map language codes to language names
  const languageCodeToName = {
    'en': 'English',
    'ar': 'Arabic',
    'es': 'Spanish',
    'fr': 'French',
  };

  // Initialize currentLanguage from localStorage or default
  const getInitialLanguage = () => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    return languageCodeToName[savedLanguage] || 'English';
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    const languageName = languageCodeToName[savedLanguage] || 'English';
    setCurrentLanguage(languageName);
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  const changeLanguage = (languageName) => {
    const languageCode = languageMap[languageName] || 'en';
    setCurrentLanguage(languageName);
    i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
    
    // Update document direction for RTL languages
    if (languageCode === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', languageCode);
    }
  };

  // Set initial direction
  useEffect(() => {
    const currentLang = i18n.language || 'en';
    if (currentLang === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', currentLang);
    }
  }, [i18n.language]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

